import { useState, useEffect, useCallback } from 'react'
import { getPaymentStatus } from '../api/getPaymentStatus'
import { PaymentResult } from '../types/PaymentResult'
import { createLogger } from '../utils/logger'

const POLLING_INTERVAL = 5000 // 5 секунд

const logger = createLogger('usePaymentResultPolling')

export const usePaymentResultPolling = (
    paymentPageId: string,
    transactionId: number,
    enabled: boolean = true,
) => {
    const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const checkStatus = useCallback(async () => {
        try {
            logger.debug(`Polling status: paymentPageId=${paymentPageId}, transactionId=${transactionId}`)

            const result = await getPaymentStatus(paymentPageId, transactionId)

            logger.debug(`Polling response received: status=${result.status}`)

            setPaymentResult(result)
            setError(false)

            // Если статус финальный, останавливаем polling
            if (result.status === 'success' || result.status === 'failed') {
                logger.info(`Final status reached via polling: status=${result.status}`)
                setLoading(false)
                return true
            }

            // Продолжаем polling
            return false
        } catch (err) {
            logger.error('Polling error occurred', err)

            setError(true)
            setLoading(false)

            // Останавливаем polling при ошибке
            return true
        }
    }, [paymentPageId, transactionId])

    useEffect(() => {
        if (!enabled) {
            logger.debug('Polling disabled')

            setLoading(false)

            return
        }

        logger.info(`Polling enabled, interval=${POLLING_INTERVAL}ms`)

        let intervalId: NodeJS.Timeout | null = null
        let mounted = true

        const startPolling = async () => {
            // Первый запрос
            const shouldStop = await checkStatus()

            if (shouldStop || !mounted) {
                logger.debug('Polling stopped after initial request')

                setLoading(false)

                return
            }

            setLoading(false)

            // Запускаем polling
            intervalId = setInterval(async () => {
                const shouldStop = await checkStatus()

                if (shouldStop && intervalId) {
                    logger.info('Polling stopped (final status or error)')

                    clearInterval(intervalId)
                }
            }, POLLING_INTERVAL)
        }

        startPolling()

        return () => {
            mounted = false

            if (intervalId) {
                logger.debug('Cleanup: clearing polling interval')

                clearInterval(intervalId)
            }
        }
    }, [checkStatus, enabled])

    return { paymentResult, loading, error }
}
